import { openDB, type DBSchema } from "idb";
import { Attempt, type AnswerInput, type StateInput } from "@ca/shared";
import type { TestBundle } from "@ca/content/bundle";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
export interface AttemptRecord {
  key: string;
  accountId: string;
  attempt: Attempt;
  bundle: TestBundle | null;
  receivedAt: number;
}
export interface AttemptWrite {
  key: string;
  accountId: string;
  attemptId: string;
  operation: "answers" | "state" | "grade" | "submit";
  method: string;
  path: string;
  body: Record<string, unknown>;
  blocked: boolean;
  error: string | null;
}
interface Store extends DBSchema {
  records: { key: string; value: AttemptRecord };
  writes: { key: string; value: AttemptWrite; indexes: { attempt: string } };
}
function optimistic(attempt: Attempt, op: AttemptWrite): Attempt {
  const next = { ...attempt, rev: attempt.rev + 1 };
  if (op.operation === "answers") {
    const changed = op.body.answers as AnswerInput["answers"];
    next.answers = [
      ...attempt.answers.filter((a) => !changed.some((c) => c.questionId === a.questionId)),
      ...changed.map((a) => ({ ...a, graded: false })),
    ];
  }
  if (op.operation === "state" && op.body.state)
    next.state = (op.body as StateInput).state ?? next.state;
  return next;
}
export class AttemptSync {
  private store;
  private flight: Promise<void> | null = null;
  constructor(
    name = "ca-attempts",
    privateSend: typeof api = api,
    privateChange: () => void = () => window.dispatchEvent(new Event("ca-attempt-sync")),
  ) {
    this.send = privateSend;
    this.change = privateChange;
    this.store = openDB<Store>(name, 1, {
      upgrade(db) {
        db.createObjectStore("records", { keyPath: "key" });
        db.createObjectStore("writes", { keyPath: "key" }).createIndex("attempt", "attemptId");
      },
    });
  }
  private send: typeof api;
  private change: () => void;
  async read(uid: string, id: string) {
    return (await this.store).get("records", `${uid}:${id}`);
  }
  async writes(uid: string, id: string) {
    return (await (await this.store).getAllFromIndex("writes", "attempt", id))
      .filter((w) => w.accountId === uid)
      .sort((a, b) => Number(a.body.expectedRev) - Number(b.body.expectedRev));
  }
  async cache(uid: string, attempt: Attempt, bundle: TestBundle | null = null) {
    const db = await this.store,
      tx = db.transaction(["records", "writes"], "readwrite"),
      key = `${uid}:${attempt.id}`;
    const old = await tx.objectStore("records").get(key),
      pending = (await tx.objectStore("writes").index("attempt").getAll(attempt.id)).filter(
        (w) => w.accountId === uid,
      );
    // A poll may never overwrite local edits, including blocked ones.
    if (!pending.length)
      await tx.objectStore("records").put({
        key,
        accountId: uid,
        attempt,
        receivedAt: Date.now(),
        bundle: bundle ?? old?.bundle ?? null,
      });
    else if (bundle && old) await tx.objectStore("records").put({ ...old, bundle });
    await tx.done;
    this.change();
  }
  async enqueue(
    uid: string,
    id: string,
    operation: AttemptWrite["operation"],
    body: Record<string, unknown>,
    expectedRev: number,
  ) {
    const db = await this.store,
      tx = db.transaction(["records", "writes"], "readwrite"),
      key = `${uid}:${id}`,
      record = await tx.objectStore("records").get(key);
    if (!record || record.attempt.rev !== expectedRev) {
      tx.abort();
      await tx.done.catch(() => {});
      throw new Error("Bài đang thay đổi ở tab khác. Tải lại bản đã lưu trên máy.");
    }
    if (record.attempt.status !== "in_progress") {
      tx.abort();
      await tx.done.catch(() => {});
      throw new Error("Bài đã kết thúc.");
    }
    const op: AttemptWrite = {
      key: uuidv7(),
      accountId: uid,
      attemptId: id,
      operation,
      method: operation === "answers" ? "PUT" : operation === "state" ? "PATCH" : "POST",
      path: `/v1/attempts/${id}/${operation === "grade" ? "answers/grade" : operation}`,
      body: { ...body, expectedRev },
      blocked: false,
      error: null,
    };
    await tx.objectStore("writes").put(op);
    await tx.objectStore("records").put({ ...record, attempt: optimistic(record.attempt, op) });
    await tx.done;
    this.change();
    return op.key;
  }
  async drain(uid: string, id: string) {
    if (this.flight) {
      await this.flight;
      if ((await this.writes(uid, id)).some((w) => !w.blocked && !w.error))
        await this.drain(uid, id);
      return;
    }
    const work = async () => {
      for (const op of await this.writes(uid, id)) {
        if (op.blocked) break;
        try {
          const response = Attempt.parse(
            await this.send(op.path, {
              method: op.method,
              body: op.body,
              key: op.key,
              accountId: uid,
            }),
          );
          const db = await this.store,
            tx = db.transaction(["writes", "records"], "readwrite"),
            key = `${uid}:${id}`,
            old = await tx.objectStore("records").get(key);
          if (!(await tx.objectStore("writes").get(op.key))) {
            await tx.done;
            continue;
          }
          await tx.objectStore("writes").delete(op.key);
          const rest = (await tx.objectStore("writes").index("attempt").getAll(id))
            .filter((w) => w.accountId === uid)
            .sort((a, b) => Number(a.body.expectedRev) - Number(b.body.expectedRev));
          if (old)
            await tx
              .objectStore("records")
              .put({ ...old, receivedAt: Date.now(), attempt: rest.reduce(optimistic, response) });
          await tx.done;
          this.change();
        } catch (e) {
          const db = await this.store,
            current = await db.get("writes", op.key);
          if (current)
            await db.put("writes", {
              ...current,
              error: e instanceof Error ? e.message : "Chưa kết nối được.",
              blocked:
                e instanceof HttpError &&
                e.status >= 400 &&
                e.status < 500 &&
                ![401, 429].includes(e.status),
            });
          this.change();
          break;
        }
      }
    };
    this.flight = (
      typeof navigator !== "undefined" && typeof navigator.locks !== "undefined"
        ? navigator.locks.request(`ca-attempt:${uid}:${id}`, work)
        : work()
    ).finally(() => {
      this.flight = null;
    });
    await this.flight;
  }
  async useServer(uid: string, attempt: Attempt) {
    const db = await this.store,
      tx = db.transaction(["records", "writes"], "readwrite"),
      key = `${uid}:${attempt.id}`,
      old = await tx.objectStore("records").get(key);
    for (const w of await tx.objectStore("writes").index("attempt").getAll(attempt.id))
      if (w.accountId === uid) await tx.objectStore("writes").delete(w.key);
    await tx
      .objectStore("records")
      .put({ key, accountId: uid, attempt, receivedAt: Date.now(), bundle: old?.bundle ?? null });
    await tx.done;
    this.change();
  }
}
export const attemptSync = new AttemptSync();
